/**
 * The search ring (2026-09-02).
 *
 * User: "I'd like there to be a spinning search circle when searching for a
 * game."
 *
 * The search overlay had a heading, a bare `12s` counter and a Cancel button.
 * A number that increments once a second is information, but it is not
 * *motion* — and the thing this screen has to communicate during an up-to-45s
 * silent server wait is that something is still happening. A ticking number
 * looks the same whether the queue is working or the socket died three
 * seconds ago.
 *
 * The count moves inside the ring rather than beside it, so the motion and
 * the number are one object instead of two things to look at.
 *
 * Built as SVG rather than a bordered div: a CSS `border-top-color` spinner
 * cannot round its own ends, and the square-cut arc is the tell that gives
 * those away as a bootstrap widget. `stroke-linecap="round"` is the whole
 * difference.
 *
 * Pure, because the arithmetic is the part that goes wrong — a dash array
 * computed against the wrong circumference leaves a visible seam or an arc
 * that laps itself.
 */

/** Viewport-relative geometry; the SVG scales to whatever box it is given. */
export const VIEW_SIZE = 100;
export const OUTER_RADIUS = 44;
export const INNER_RADIUS = 33;

/**
 * `stroke-dasharray` for an arc covering `fraction` of a circle.
 *
 * Returned as dash-then-gap so the remainder is exact: computing only the dash
 * and letting the gap default would repeat the pattern around the circle.
 */
export function arcDash(radius: number, fraction: number): string {
  const safeRadius = Number.isFinite(radius) && radius > 0 ? radius : 1;
  const safeFraction = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  const circumference = 2 * Math.PI * safeRadius;
  const dash = circumference * safeFraction;
  return `${dash.toFixed(2)} ${(circumference - dash).toFixed(2)}`;
}

/**
 * Two arcs turning against each other.
 *
 * One ring reads as a loading spinner; two at different speeds and opposite
 * directions read as a mechanism working, which is what a search screen in a
 * dungeon game should look like. The inner one is dimmer so the eye still has
 * somewhere to rest.
 *
 * Split out from `searchRingMarkup` (2026-09-08) so the entry veil — the screen
 * that covers the load between accepting a match and the world existing — turns
 * the same mechanism rather than a second one drawn to nearly the same numbers.
 * The geometry has exactly one definition, and `arcDash` is the reason: two
 * copies of that arithmetic is two chances at a seam.
 */
export function spinnerArcsMarkup(): string {
  return `
      <svg viewBox="0 0 ${VIEW_SIZE} ${VIEW_SIZE}" class="absolute inset-0 w-full h-full search-ring-outer"
           aria-hidden="true" focusable="false">
        <circle cx="50" cy="50" r="${OUTER_RADIUS}" fill="none" stroke="#2b2219" stroke-width="5" />
        <circle cx="50" cy="50" r="${OUTER_RADIUS}" fill="none" stroke="#e0b25f" stroke-width="5"
                stroke-linecap="round" stroke-dasharray="${arcDash(OUTER_RADIUS, 0.28)}" />
      </svg>
      <svg viewBox="0 0 ${VIEW_SIZE} ${VIEW_SIZE}" class="absolute inset-0 w-full h-full search-ring-inner"
           aria-hidden="true" focusable="false">
        <circle cx="50" cy="50" r="${INNER_RADIUS}" fill="none" stroke="#8a6c45" stroke-width="2.5"
                stroke-linecap="round" stroke-dasharray="${arcDash(INNER_RADIUS, 0.16)}" />
      </svg>
  `;
}

/** The search overlay's ring: the mechanism above, with the elapsed count living inside it rather than beside it. */
export function searchRingMarkup(elapsedLabelId: string): string {
  return `
    <div class="relative mx-auto w-[136px] h-[136px]">
      ${spinnerArcsMarkup()}
      <div class="absolute inset-0 grid place-items-center">
        <p id="${elapsedLabelId}" class="text-3xl font-heading text-gold tabular-nums" role="status" aria-live="polite">0s</p>
      </div>
    </div>
  `;
}
